import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { ChatInputActionsComponent } from "../chat-input-actions/chat-input-actions.component";
import { ChatService, ChatMessage } from '../chat.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-chat-messages',
  standalone: true,
  imports: [CommonModule , ChatInputActionsComponent],
  templateUrl: './chat-messages.component.html',
  styleUrl: './chat-messages.component.css'
})
export class ChatMessagesComponent implements OnInit, OnDestroy, OnChanges {
   @Input() value: any;
  @Output() back = new EventEmitter<void>();
  @ViewChild('messageContainer') messageContainer?: ElementRef<HTMLDivElement>;
  readonly defaultProfileImage = 'https://media.istockphoto.com/id/1495088043/vector/user-profile-icon-avatar-or-person-icon-profile-picture-portrait-symbol-default-portrait.jpg?s=612x612&w=0&k=20&c=dhV2p1JwmloBTOaGAtaA3AW1KSnjsdMt7-U_3EZElZ0=';
  messages: ChatMessage[] = [];
  private subscription: Subscription = new Subscription();

  // Image modal properties
  showImageModal = false;
  modalImageUrl = '';
  modalImageName = '';

  // Audio playback properties
  private currentAudio: HTMLAudioElement | null = null;
  private currentMessageId: string | null = null;
  private playingMessages: Set<string> = new Set();
  private audioDurations: Map<string, number> = new Map();

  constructor(private chatService: ChatService) {}

  ngOnInit() {
    this.subscription = this.chatService.messages$.subscribe(messages => {
      this.messages = messages;
      this.scheduleScrollToBottom();
    });

    // Initialize room if value contains room information
    if (this.value && this.value.studentId && this.value.userId) {
      const userId = this.value.userId;
      const userType = this.value.userType || 'teacher';
      this.chatService.initializeRoom(this.value.studentId, userId, userType);
      this.scheduleScrollToBottom();
      // Mark all messages as seen after a short delay to ensure messages are loaded
      setTimeout(() => this.chatService.markAllMessagesAsSeen(), 1000);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value'] && !changes['value'].firstChange) {
      if (this.value && this.value.studentId && this.value.userId) {
        const userId = this.value.userId;
        const userType = this.value.userType || 'teacher';
        this.chatService.initializeRoom(this.value.studentId, userId, userType);
        this.scheduleScrollToBottom();
        // Mark all messages as seen after a short delay
        setTimeout(() => this.chatService.markAllMessagesAsSeen(), 1000);
      } else {
        this.chatService.resetRoom();
      }
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.chatService.resetRoom();
  }

  trackByMessageId(index: number, message: ChatMessage): string | number {
    return message.id;
  }

  scrollToBottom() {
    const container = this.messageContainer?.nativeElement;
    if (!container) return;

    container.scrollTop = container.scrollHeight;
  }

  private scheduleScrollToBottom() {
    setTimeout(() => this.scrollToBottom());
    requestAnimationFrame(() => this.scrollToBottom());
  }

  openImage(imageUrl: string) {
    // Open image in a new tab or modal
    window.open(imageUrl, '_blank');
  }

  downloadFile(fileUrl: string, fileName: string) {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  }

  toggleAudio(message: ChatMessage) {
    const messageKey = message.id.toString();
    if (message.audioBlob) {
      if (this.isPlaying(messageKey)) {
        // Pause the audio
        if (this.currentAudio && this.currentMessageId === messageKey) {
          this.currentAudio.pause();
        }
        this.playingMessages.delete(messageKey);
      } else {
        // If a different audio is playing, stop it
        if (this.currentAudio && this.currentMessageId !== messageKey) {
          this.currentAudio.pause();
          this.currentAudio.currentTime = 0;
          this.playingMessages.delete(this.currentMessageId!);
        }

        // Create new audio if none is playing or different message
        if (!this.currentAudio || this.currentMessageId !== messageKey) {
          const audioUrl = URL.createObjectURL(message.audioBlob);
          this.currentAudio = new Audio(audioUrl);
          this.currentMessageId = messageKey;

          // Clean up when audio ends
          this.currentAudio.onended = () => {
            this.currentAudio = null;
            this.currentMessageId = null;
            this.playingMessages.delete(messageKey);
          };
        }

        this.currentAudio.play();
        this.playingMessages.add(messageKey);
      }
    }
  }

  isPlaying(messageId: string): boolean {
    return this.playingMessages.has(messageId);
  }

  getAudioDuration(message: ChatMessage): string {
    const messageKey = message.id.toString();
    if (message.audioBlob) {
      if (this.audioDurations.has(messageKey)) {
        return this.formatDuration(this.audioDurations.get(messageKey)!);
      } else {
        // Create a temporary audio element to get duration
        const audioUrl = URL.createObjectURL(message.audioBlob);
        const tempAudio = new Audio(audioUrl);
        tempAudio.onloadedmetadata = () => {
          this.audioDurations.set(messageKey, tempAudio.duration);
          URL.revokeObjectURL(audioUrl);
        };
        return '0:00'; // Default while loading
      }
    }
    return '0:00';
  }

  private formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  getAudioProgress(messageId: number | string): number {
    const messageKey = messageId.toString();
    if (this.currentAudio && this.currentMessageId === messageKey) {
      const duration = this.audioDurations.get(messageKey);
      if (duration && duration > 0) {
        return (this.currentAudio.currentTime / duration) * 100;
      }
    }
    return 0;
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'ppt':
      case 'pptx': return '📽️';
      case 'zip':
      case 'rar': return '📦';
      case 'txt': return '📄';
      default: return '📎';
    }
  }

  getFileSize(fileUrl: string): string {
    // For demo purposes, return a mock size
    // In real implementation, you'd calculate actual file size
    return '2.3 MB';
  }

  formatTime(time: string): string {
    if (!time) return '';
    const date = new Date(time);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  getMessageStatusIcon(status?: string): string {
    switch (status) {
      case 'sending': return 'ri-time-line';
      case 'sent': return 'ri-check-line';
      case 'delivered': return 'ri-check-double-line';
      case 'read': return 'ri-check-double-line';
      default: return 'ri-check-double-line';
    }
  }

  getMessageStatusColor(status?: string): string {
    switch (status) {
      case 'sending': return 'text-gray-400';
      case 'sent': return 'text-gray-400';
      case 'delivered': return 'text-gray-400';
      case 'read': return 'text-blue-500';
      default: return 'text-gray-400';
    }
  }

  openImageModal(imageUrl: string, imageName?: string) {
    this.modalImageUrl = imageUrl;
    this.modalImageName = imageName || 'Image';
    this.showImageModal = true;
  }

  closeImageModal() {
    this.showImageModal = false;
    this.modalImageUrl = '';
    this.modalImageName = '';
  }

  canPreviewFile(fileName: string): boolean {
    const extension = fileName.split('.').pop()?.toLowerCase();
    // File types that can be previewed in browser
    const previewableTypes = ['pdf', 'txt', 'md', 'json', 'xml', 'html', 'css', 'js', 'ts'];
    return previewableTypes.includes(extension || '');
  }

  previewFile(fileUrl: string, fileName: string) {
    // For now, open in new tab. In production, you might want to show in a modal
    window.open(fileUrl, '_blank');
  }
}
